/**
 * HistoriqueVentes — port BE-TAYE / vue expert-comptable.
 *
 * Filtres avancés (période, client, statut, fourchette TTC), totaux calculés,
 * export Excel (xlsx), détection des trous de numérotation (billing_gaps).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import {
  Download, Search, RefreshCw, AlertTriangle, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";

const STATUS_FR = {
  draft: "Brouillon",
  issued: "Émise",
  paid: "Payée",
  cancelled: "Annulée",
  overdue: "En retard",
};

export default function HistoriqueVentes() {
  const currentYear = new Date().getFullYear();
  const [invoices, setInvoices] = useState([]);
  const [totals, setTotals] = useState({ total_ht: 0, total_ttc: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date_from: `${currentYear}-01-01`,
    date_to: `${currentYear}-12-31`,
    status: "all", min_ttc: "", max_ttc: "", search: "",
  });
  const [gaps, setGaps] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.min_ttc) params.min_ttc = Number(filters.min_ttc);
      if (filters.max_ttc) params.max_ttc = Number(filters.max_ttc);
      const { data } = await api.get("/invoices/history", { params });
      setInvoices(data.invoices || []);
      setTotals({ total_ht: data.total_ht, total_ttc: data.total_ttc, count: data.count });
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const loadGaps = async () => {
    try {
      const year = parseInt((filters.date_from || `${currentYear}`).slice(0, 4), 10);
      const { data } = await api.get("/invoices/billing-gaps", { params: { year } });
      setGaps(data);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); loadGaps(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((i) =>
      (i.number || "").toLowerCase().includes(q) ||
      (i.client_name || "").toLowerCase().includes(q));
  }, [invoices, filters.search]);

  const exportExcel = () => {
    const rows = filtered.map((i) => ({
      Numero: i.number, Date: i.issued_at, Echeance: i.due_date, Statut: i.status,
      Client: i.client_name, "Total HT": i.total_ht, TVA: i.total_vat,
      "Total TTC": i.total_ttc, Source: i.imported_from || "native",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ventes");
    XLSX.writeFile(wb, `historique-ventes-${filters.date_from}_${filters.date_to}.xlsx`);
    toast.success("Export téléchargé");
  };

  return (
    <div className="space-y-6" data-testid="historique-ventes-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Historique Ventes
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Vue exhaustive avec filtres avancés — export Excel pour l'expert-comptable.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
          <Button onClick={exportExcel} disabled={!filtered.length}
                   className="bg-emerald-600 hover:bg-emerald-700 text-white"
                   data-testid="history-export-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Exporter Excel
          </Button>
        </div>
      </div>

      {gaps && gaps.gap_count > 0 && (
        <Alert className="border-amber-200 bg-amber-50" data-testid="billing-gaps-alert">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm text-amber-800">
            <strong>{gaps.gap_count} trou(s) dans la numérotation {gaps.year}</strong> —
            plage {gaps.min} → {gaps.max}. Numéros manquants :
            <span className="font-mono ml-1">{gaps.gaps.slice(0, 10).join(", ")}</span>
            {gaps.gap_count > 10 && ` (+${gaps.gap_count - 10} autres)`}.
            Ces trous indiquent des annulations ou des factures supprimées à documenter.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Du</Label>
            <div className="mt-1">
              <DatePickerFR value={filters.date_from}
                             onChange={(v) => setFilters({ ...filters, date_from: v })}
                             data-testid="history-date-from" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Au</Label>
            <div className="mt-1">
              <DatePickerFR value={filters.date_to}
                             onChange={(v) => setFilters({ ...filters, date_to: v })}
                             data-testid="history-date-to" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Statut</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="draft">Brouillon</SelectItem>
                <SelectItem value="issued">Émise</SelectItem>
                <SelectItem value="paid">Payée</SelectItem>
                <SelectItem value="cancelled">Annulée</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">TTC min</Label>
            <Input type="number" value={filters.min_ttc}
                    onChange={(e) => setFilters({ ...filters, min_ttc: e.target.value })}
                    className="mt-1 font-mono" placeholder="0"
                    data-testid="history-min-ttc" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">TTC max</Label>
            <Input type="number" value={filters.max_ttc}
                    onChange={(e) => setFilters({ ...filters, max_ttc: e.target.value })}
                    className="mt-1 font-mono" placeholder="∞"
                    data-testid="history-max-ttc" />
          </div>
          <div className="flex items-end">
            <Button onClick={load} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
              <Search className="w-4 h-4 mr-1.5" /> Filtrer
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-500">Factures</p>
          <p className="text-2xl font-bold font-mono">{totals.count}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-500">Total HT</p>
          <p className="text-2xl font-bold font-mono">{fmtEUR(totals.total_ht)}</p>
        </CardContent></Card>
        <Card className="border-blue-200 bg-blue-50"><CardContent className="p-4">
          <p className="text-xs text-blue-700">Total TTC</p>
          <p className="text-2xl font-bold font-mono text-blue-900"
              data-testid="history-total-ttc">{fmtEUR(totals.total_ttc)}</p>
        </CardContent></Card>
      </div>

      <Card>
        <div className="p-3 border-b">
          <Input placeholder="Recherche numéro ou client…" value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="max-w-sm" data-testid="history-search" />
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Numéro</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">HT</TableHead>
              <TableHead className="text-right">TVA</TableHead>
              <TableHead className="text-right">TTC</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((i) => (
              <TableRow key={i.id} className="hover:bg-slate-50"
                         data-testid={`history-row-${i.id}`}>
                <TableCell><Link to={`/app/invoices/${i.id}`}
                                  className="font-mono text-xs text-blue-700 hover:underline">
                  {i.number || "brouillon"}
                </Link></TableCell>
                <TableCell className="font-mono text-sm">{fmtDate(i.issued_at)}</TableCell>
                <TableCell className="text-sm">{i.client_name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="uppercase text-[10px]">{STATUS_FR[i.status] || i.status}</Badge>
                </TableCell>
                <TableCell className="text-right font-mono text-sm">{fmtEUR(i.total_ht)}</TableCell>
                <TableCell className="text-right font-mono text-sm text-slate-500">{fmtEUR(i.total_vat)}</TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">{fmtEUR(i.total_ttc)}</TableCell>
              </TableRow>
            ))}
            {!filtered.length && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">
                Aucune facture ne correspond aux filtres.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
